import Konva from 'konva';
import type { ComponentDef } from '../../core/library/component-def.js';

/**
 * Renderer del cuerpo de un pulsador tipo tact-switch de 6×6 mm visto
 * DESDE ARRIBA (superficie donde se ve la placa metálica cuadrada, los
 * cuatro remaches negros de sujeción y el actuador redondo central).
 *
 * Al invertir (`flipped=true`) sólo se ve el cuerpo negro plano — la
 * placa metálica, los remaches y el actuador están en la CARA superior,
 * la que queda oculta cuando el pulsador se ve por debajo.
 */
export function renderPushButtonBody(
  def: ComponentDef,
  _labelText: string,
  _rotationDeg: number = 0,
  flipped: boolean = false,
): Konva.Group {
  const g = new Konva.Group();

  const w = def.body.length_mm ?? 6.35;
  const h = def.body.width_mm ?? 6.35;
  const cr = def.body.corner_radius_mm ?? 0.5;
  const bodyColor = def.appearance.body_color ?? '#1a1a1a';
  const metalColor = def.appearance.metal_color ?? '#c8c8c8';

  // Cuerpo negro cuadrado (base de plástico). Va con gradiente muy sutil
  // para no parecer un bloque plano.
  g.add(new Konva.Rect({
    x: -w / 2, y: -h / 2, width: w, height: h,
    cornerRadius: cr,
    fillLinearGradientStartPoint: { x: 0, y: -h / 2 },
    fillLinearGradientEndPoint: { x: 0, y: h / 2 },
    fillLinearGradientColorStops: [
      0, mix(bodyColor, '#ffffff', 0.10),
      1, mix(bodyColor, '#000000', 0.15),
    ],
    stroke: '#000000',
    strokeWidth: 0.12,
    listening: true,
  })).setAttr('nodeKind', 'body');

  if (flipped) {
    // Vista por debajo: sólo el cuerpo plano. Terminamos aquí.
    return g;
  }

  // Placa metálica plateada centrada, un pelín más pequeña que el
  // cuerpo para dejar visible el marco negro.
  const plateInset = Math.min(w, h) * 0.10;
  const pW = w - plateInset * 2;
  const pH = h - plateInset * 2;
  g.add(new Konva.Rect({
    x: -pW / 2, y: -pH / 2, width: pW, height: pH,
    cornerRadius: cr * 0.6,
    fillLinearGradientStartPoint: { x: -pW / 2, y: -pH / 2 },
    fillLinearGradientEndPoint: { x: pW / 2, y: pH / 2 },
    fillLinearGradientColorStops: [
      0, mix(metalColor, '#ffffff', 0.35),
      0.5, metalColor,
      1, mix(metalColor, '#000000', 0.35),
    ],
    stroke: '#00000060',
    strokeWidth: 0.08,
    listening: false,
  }));

  // Cuatro remaches negros en las esquinas interiores de la placa
  // metálica (los puntitos de sujeción de la carcasa).
  const rivetR = Math.min(w, h) * 0.09;
  const rivetInset = Math.min(w, h) * 0.20;
  for (const [rx, ry] of [
    [-w / 2 + rivetInset,  -h / 2 + rivetInset],
    [ w / 2 - rivetInset,  -h / 2 + rivetInset],
    [-w / 2 + rivetInset,   h / 2 - rivetInset],
    [ w / 2 - rivetInset,   h / 2 - rivetInset],
  ]) {
    g.add(new Konva.Circle({
      x: rx, y: ry, radius: rivetR,
      fillRadialGradientStartPoint: { x: -rivetR * 0.3, y: -rivetR * 0.3 },
      fillRadialGradientEndPoint: { x: 0, y: 0 },
      fillRadialGradientStartRadius: 0,
      fillRadialGradientEndRadius: rivetR,
      fillRadialGradientColorStops: [0, '#3a3a3a', 1, '#0a0a0a'],
      stroke: '#000000',
      strokeWidth: 0.05,
      listening: false,
    }));
  }

  // Actuador redondo central (el "botón" que se aprieta): negro con
  // ligero brillo diagonal para dar volumen.
  const buttonR = Math.min(w, h) * 0.28;
  g.add(new Konva.Circle({
    x: 0, y: 0, radius: buttonR,
    fillRadialGradientStartPoint: { x: -buttonR * 0.35, y: -buttonR * 0.35 },
    fillRadialGradientEndPoint: { x: 0, y: 0 },
    fillRadialGradientStartRadius: 0,
    fillRadialGradientEndRadius: buttonR,
    fillRadialGradientColorStops: [
      0, '#4a4a4a',
      0.55, '#1a1a1a',
      1, '#000000',
    ],
    stroke: '#000000',
    strokeWidth: 0.08,
    listening: false,
  }));
  // Reflejo especular tenue en la parte superior-izquierda del actuador.
  g.add(new Konva.Ellipse({
    x: -buttonR * 0.3, y: -buttonR * 0.3,
    radiusX: buttonR * 0.35, radiusY: buttonR * 0.18,
    rotation: -35,
    fill: '#ffffff',
    opacity: 0.18,
    listening: false,
  }));

  return g;
}

function mix(a: string, b: string, t: number): string {
  const pa = hex(a), pb = hex(b);
  const r = Math.round(pa[0] + (pb[0] - pa[0]) * t);
  const g = Math.round(pa[1] + (pb[1] - pa[1]) * t);
  const bl = Math.round(pa[2] + (pb[2] - pa[2]) * t);
  return `#${((1 << 24) + (r << 16) + (g << 8) + bl).toString(16).slice(1)}`;
}
function hex(h: string): [number, number, number] {
  const s = h.replace('#', '');
  const v = parseInt(s.length === 3 ? s.split('').map((c) => c + c).join('') : s, 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}
